import {
  Button,
  Chip,
  Group,
  List,
  Select,
  Stack,
  Stepper,
  Text,
  TextInput,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconCalendar, IconUser, IconUserSearch } from "@tabler/icons-react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { fetchAuthSession } from "aws-amplify/auth";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useMemo, useState } from "react";
import { client } from "@/api-client";
import { validationErrors } from "@/api-client/errors";
import type { Day, HttpValidationError, PublicProfileInput } from "@/api-types";
import { Header } from "@/components/Header";
import { usePublicProfileForm } from "@/components/PublicProfileForm";
import { initFCMPassive } from "@/fcm";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";
import {
  COUNTRY_OPTIONS,
  getCurrentCountry,
  listSupportedCountries,
} from "@/utils/country";
import { daysOptions, parseTimeStringToNumber } from "./common";
import { personalInfoSchema, workScheduleSchema } from "./schemas";

export const NewProfile = () => {
  const [active, setActive] = useState(0);
  const { navigate } = useNavigator();
  const geocodingLibrary = useMapsLibrary("geocoding");
  const geocoding = useMemo(
    () => geocodingLibrary && new geocodingLibrary.Geocoder(),
    [geocodingLibrary],
  );

  const user = useUser();

  useEffect(() => {
    if (user.isProfileCreated) {
      navigate({ path: UniversalRoutes.HOME });
    }
  }, [user.isProfileCreated, navigate]);

  // Step 1: Personal Information
  const personalInfoForm = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      phone: "",
      address: "",
      locality: "",
      postalCode: "",
      country: getCurrentCountry(),
      state: "",
    },
    validate: zod4Resolver(personalInfoSchema),
  });

  // Step 2: Working Days and Hours
  const workScheduleForm = useForm({
    initialValues: {
      workdays: daysOptions
        .map((day, index) => (index < 5 ? day.value : null))
        .filter((day) => day) as string[], // Preselect weekdays
      startWorkTime: "09:00",
      endWorkTime: "17:00",
    },
    validate: zod4Resolver(workScheduleSchema),
  });

  // Step 3: Public Profile (state-based)
  const [publicProfileState, setPublicProfileState] =
    useState<PublicProfileInput>({
      headline: "",
      description: "",
      specialities: [],
      languages: [],
      testimonials: [],
      education: [],
      links: [],
    });

  const publicProfileForm = usePublicProfileForm({
    initialValues: publicProfileState,
    mode: "minimal",
    onFormSubmit: (values, _isPreview) => {
      setPublicProfileState(values);
    },
  });

  const createTrainerProfile = client.useMutation("post", "/trainer/profile/");
  const { error } = createTrainerProfile;

  const handleSubmit = async () => {
    const personalValues = personalInfoForm.values;
    const workValues = workScheduleForm.values;
    const profileValues = publicProfileState;

    let location: { lat: number; lng: number } | undefined;

    if (geocoding) {
      try {
        const { results } = await geocoding.geocode({
          address: [
            personalValues.address,
            personalValues.postalCode,
            personalValues.locality,
            personalValues.country,
          ].join(" "),
        });
        const result = results?.[0];
        if (result) {
          location = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };
        }
      } catch (error) {
        console.error("Geocoding error:", error);
      }
    }

    try {
      await createTrainerProfile.mutateAsync({
        body: {
          personalInfo: {
            ...personalValues,
            location,
          },
          workSchedule: {
            workdays: workValues.workdays.filter((day) => day) as Day[],
            startWorkTime: parseTimeStringToNumber(workValues.startWorkTime),
            endWorkTime: parseTimeStringToNumber(workValues.endWorkTime),
          },
          publicProfile: {
            ...profileValues,
            languages: [],
            testimonials: [],
            education: [],
            links: [],
          },
        },
      });

      await fetchAuthSession({ forceRefresh: true });
      await initFCMPassive();
    } catch (error) {
      console.error("Error creating trainer profile:", error);
    }
  };

  const nextStep = () => {
    if (active === 0) {
      personalInfoForm.validate();
      if (personalInfoForm.isValid()) {
        setActive((current) => current + 1);
      }
    } else if (active === 1) {
      if (publicProfileForm.submit()) {
        setActive((current) => current + 1);
      }
    } else {
      workScheduleForm.validate();
      if (workScheduleForm.isValid()) {
        handleSubmit();
      }
    }
  };

  const prevStep = () => setActive((current) => current - 1);

  const createProfileError =
    error &&
    (typeof error.detail === "string" ? (
      <Text fw={"bold"} c={"var(--mantine-color-error)"}>
        {error.detail}
      </Text>
    ) : (
      <>
        <Text fw={"bold"} c={"var(--mantine-color-error)"}>
          Some fields have errors, please correct them and try again.
        </Text>
        <List c={"var(--mantine-color-error)"}>
          {validationErrors(error as HttpValidationError).map(
            (e, index: number) => (
              <List.Item key={index}>
                {e.field}: {e.message}
              </List.Item>
            ),
          )}
        </List>
      </>
    ));

  return (
    <Stack>
      <Header title="Create Your Profile" backButton={false} />
      <Stepper active={active} orientation="vertical">
        <Stepper.Step
          label="Personal Information"
          description="Tell us about yourself"
          icon={<IconUser />}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              nextStep();
            }}
            id="personal-form"
          >
            <Stack>
              <TextInput
                withAsterisk
                label="First name"
                autoComplete="given-name"
                {...personalInfoForm.getInputProps("firstName")}
              />
              <TextInput
                withAsterisk
                label="Last name"
                autoComplete="family-name"
                {...personalInfoForm.getInputProps("lastName")}
              />
              <TextInput
                withAsterisk
                label="Phone"
                autoComplete="tel"
                {...personalInfoForm.getInputProps("phone")}
              />
              <Select
                withAsterisk
                label="Country"
                key={personalInfoForm.key("country")}
                disabled
                data={listSupportedCountries().map((c) => COUNTRY_OPTIONS[c])}
                {...personalInfoForm.getInputProps("country")}
              />
              <Select
                label="Province"
                key={personalInfoForm.key("state")}
                {...personalInfoForm.getInputProps("state")}
                data={[
                  { value: "NH", label: "Noord-Holland" },
                  { value: "ZH", label: "Zuid-Holland" },
                  { value: "GR", label: "Groningen" },
                  { value: "FR", label: "Friesland" },
                  { value: "DR", label: "Drenthe" },
                  { value: "OV", label: "Overijssel" },
                  { value: "GE", label: "Gelderland" },
                  { value: "LI", label: "Limburg" },
                  { value: "NB", label: "Noord-Brabant" },
                  { value: "UT", label: "Utrecht" },
                ]}
              />
              <TextInput
                withAsterisk
                label="Address"
                autoComplete="street-address"
                {...personalInfoForm.getInputProps("address")}
              />
              <TextInput
                withAsterisk
                label="City"
                autoComplete="address-level2"
                {...personalInfoForm.getInputProps("locality")}
              />
              <TextInput
                withAsterisk
                label="Postal code"
                autoComplete="postal-code"
                {...personalInfoForm.getInputProps("postalCode")}
              />
            </Stack>
          </form>
        </Stepper.Step>

        <Stepper.Step
          label="Public Profile"
          description="Tell clients about you and your services"
          icon={<IconUserSearch />}
        >
          <Text size="sm" c="dimmed">
            You can add this information later and skip this step.
          </Text>
          {publicProfileForm.form}
        </Stepper.Step>

        <Stepper.Step
          label="Working Days & Hours"
          description="Set your availability"
          icon={<IconCalendar />}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              nextStep();
            }}
            id="work-schedule-form"
          >
            <Stack gap="xs">
              <Text>Working Days</Text>
              <Chip.Group
                multiple
                key={workScheduleForm.key(`workdays`)}
                {...workScheduleForm.getInputProps(`workdays`)}
              >
                <Group>
                  {daysOptions.map((day, index) => (
                    <Chip key={index} value={day.value}>
                      {day.label}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>

              {workScheduleForm.errors.workdays && (
                <Text c="var(--mantine-color-error)" size="xs">
                  {workScheduleForm.errors.workdays}
                </Text>
              )}

              <TimeInput
                withAsterisk
                label="Start of working hours"
                placeholder="09:00"
                key={workScheduleForm.key("startWorkTime")}
                {...workScheduleForm.getInputProps("startWorkTime")}
              />

              <TimeInput
                withAsterisk
                label="End of working hours"
                placeholder="17:00"
                key={workScheduleForm.key("endWorkTime")}
                {...workScheduleForm.getInputProps("endWorkTime")}
              />
            </Stack>
          </form>
        </Stepper.Step>
      </Stepper>

      <Stack>
        {createProfileError}

        <Group justify="flex-end" mt="xl">
          {active !== 0 && (
            <Button
              variant="default"
              onClick={prevStep}
              disabled={createTrainerProfile.isPending}
            >
              Back
            </Button>
          )}
          <Button
            onClick={nextStep}
            loading={active === 2 ? createTrainerProfile.isPending : false}
          >
            {active === 2 ? "Create Profile" : "Next"}
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
};
